import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Ban, CheckCircle2, Eye, XCircle } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSetMerchantStatus } from "@/features/merchant/api/use-set-merchant-status";
import type { MerchantRow } from "@/features/merchant/components/merchants/merchants-columns";
import type { MerchantStatus } from "@/features/shared/api/enums";

type ActionableMerchantStatus = "ACTIVE" | "SUSPENDED" | "CLOSED";

const nextStatuses = (status?: MerchantStatus): ActionableMerchantStatus[] => {
  switch (status) {
    case "PENDING":
      return ["ACTIVE", "CLOSED"];
    case "ACTIVE":
      return ["SUSPENDED", "CLOSED"];
    case "SUSPENDED":
      return ["ACTIVE", "CLOSED"];
    case "CLOSED":
    default:
      return [];
  }
};

const STATUS_ACTION_LABEL: Record<ActionableMerchantStatus, string> = {
  ACTIVE: "merchant.merchants.actions.activate",
  SUSPENDED: "merchant.merchants.actions.suspend",
  CLOSED: "merchant.merchants.actions.close",
};

const STATUS_ACTION_ICON: Record<ActionableMerchantStatus, typeof Ban> = {
  ACTIVE: CheckCircle2,
  SUSPENDED: Ban,
  CLOSED: XCircle,
};

/**
 * Menu d'actions de ligne : voir le détail + changement de statut.
 * Les autres actions (kyc, permissions, settlement, sub-merchant) vivent
 * sur la page détail pour ne pas surcharger la ligne.
 */
export function MerchantRowActions({
  merchant,
  navigate,
}: {
  merchant: MerchantRow;
  navigate: NavigateFunction;
}) {
  const setStatus = useSetMerchantStatus();
  const [targetStatus, setTargetStatus] =
    useState<ActionableMerchantStatus | null>(null);

  const statusActions = nextStatuses(merchant.status).map((status) => ({
    id: `set-status-${status}`,
    label: t(STATUS_ACTION_LABEL[status]),
    icon: STATUS_ACTION_ICON[status],
    variant:
      status === "SUSPENDED" || status === "CLOSED"
        ? ("destructive" as const)
        : ("default" as const),
    onClick: () => setTargetStatus(status),
  }));

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("merchant.merchants.actions.view"),
            icon: Eye,
            onClick: () => navigate(`/app/merchants/${merchant.id}`),
          },
          ...statusActions,
        ]}
      />
      <MutationConfirmPreview
        open={targetStatus !== null}
        onOpenChange={(open) => {
          if (!open) setTargetStatus(null);
        }}
        data={{
          id: merchant.id,
          legalName: merchant.legalName,
          currentStatus: merchant.status,
          nextStatus: targetStatus,
        }}
        title={t("common.mutationPreview.statusTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={
          targetStatus ? t(STATUS_ACTION_LABEL[targetStatus]) : undefined
        }
        t={t}
        pending={setStatus.isPending}
        onConfirm={() => {
          if (!targetStatus) return;
          setStatus.mutate(
            { merchantId: merchant.id, status: targetStatus },
            {
              onSuccess: () => {
                notifSilence(t("merchant.merchants.feedback.statusUpdated"));
                setTargetStatus(null);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        }}
      />
    </>
  );
}
